import {
	type Address,
	agencyId,
	type BondRequestBondType,
	bondRequestBondTypeDef,
	contractBondSubtypeDef,
	Dtos,
	discriminatedUnionWithFallback,
	isCommercialSuretyBondType,
	isContractCategoryBondType,
	userId,
} from "@inrev/common";
import type { Path } from "react-hook-form";
import { z } from "zod";
import { getSchemaDefaults } from "../../../../utils/form";
import {
	commercialBondFormSchema,
	commercialBondSubmitSchema,
} from "../../../shared/bond/commercialBondSchema";
import { resolvePrincipalName, withPrincipalMatchesCompany } from "../../../shared/bond/principal";

const CreateAccount = Dtos.Admin.SuretyAccount.Create.Request;
const CreateQuote = Dtos.Admin.SuretyQuote.Create.Request;

export { commercialBondSubmitSchema };

const accountDiscriminatedUnion = discriminatedUnionWithFallback(
	"modelSize",
	[
		CreateAccount.accountSchema.aiSmallAccountSubmitSchema,
		CreateAccount.accountSchema.aiMediumAccountSubmitSchema,
		CreateAccount.accountSchema.aiLargeAccountSubmitSchema,
	],
	CreateAccount.accountSchema.aiSmallAccountSubmitSchema,
	{ discriminatorErrorMessage: "Required" },
);

export const adminAccountFormSchema = z.object({
	agencyId: agencyId,
	agentId: userId,
	underwriterId: userId,
	followers: z
		.array(userId, { error: "At least one follower is required" })
		.min(1, "At least one follower is required"),
	account: accountDiscriminatedUnion,
	accountTerms: CreateAccount.accountTermsSchema,
	indemnityAgreement: CreateAccount.indemnityAgreementSchema.extend({ name: z.string() }).nullish(),
	requiresPaymentAndPerformanceBonds: z.boolean().nullish(),
});

export const getAdminAccountFormValidationSchema = ({
	requirePaymentAndPerformanceBonds,
}: {
	requirePaymentAndPerformanceBonds: boolean;
}) =>
	requirePaymentAndPerformanceBonds
		? adminAccountFormSchema.extend({
				requiresPaymentAndPerformanceBonds: z.boolean({ error: "Required" }),
			})
		: adminAccountFormSchema;

export const adminCombinedFormSchema = adminAccountFormSchema.extend({
	quote: CreateQuote.quoteSchemaForAccountCreation,
	bondTerms: CreateQuote.optionalBondTermsSchema,
	bondType: z.enum(bondRequestBondTypeDef).nullish(),
	contractBondType: z.enum(contractBondSubtypeDef).nullish(),
	commercialBond: commercialBondFormSchema,
});

// Minimal-mode ("AI extraction") variant: amounts relaxed, only project name required — mirrors the agent combined flow.
const { minimalBidQuoteFieldOverrides, minimalFinalQuoteFieldOverrides } = Dtos.SuretyQuote.Create;

const minimalAdminQuoteSchema = z.discriminatedUnion("contractSuretyType", [
	z.object({
		...CreateQuote.adminBidQuoteSchemaForAccountCreation.shape,
		...minimalBidQuoteFieldOverrides,
	}),
	z.object({
		...CreateQuote.adminFinalQuoteSchemaForAccountCreation.shape,
		...minimalFinalQuoteFieldOverrides,
	}),
	CreateQuote.adminBidToFinalQuoteSchemaForAccountCreation,
]);

// Legacy (non-minimal) mode collects only project.description, never project.name.
const requiredDescriptionAdminProjectSchema =
	CreateQuote.adminBidQuoteSchemaForAccountCreation.shape.project.extend({
		// nullish input keeps form defaults compatible; pipe output is hard-required for asterisk derivation.
		description: z.string().nullish().pipe(z.string().trim().min(1, "Required")),
	});

const legacyAdminQuoteSchema = z.discriminatedUnion("contractSuretyType", [
	z.object({
		...CreateQuote.adminBidQuoteSchemaForAccountCreation.shape,
		project: requiredDescriptionAdminProjectSchema,
	}),
	z.object({
		...CreateQuote.adminFinalQuoteSchemaForAccountCreation.shape,
		project: requiredDescriptionAdminProjectSchema,
	}),
	CreateQuote.adminBidToFinalQuoteSchemaForAccountCreation,
]);

export const getAdminCombinedFormValidationSchema = ({
	minimalMode,
	requirePaymentAndPerformanceBonds = false,
	commercialSuretyEnabled = false,
	bondType,
}: {
	minimalMode: boolean;
	requirePaymentAndPerformanceBonds?: boolean;
	commercialSuretyEnabled?: boolean;
	bondType?: BondRequestBondType | null;
}) => {
	const quoteSchema = minimalMode ? minimalAdminQuoteSchema : legacyAdminQuoteSchema;
	const isCommercialBond = commercialSuretyEnabled && isCommercialSuretyBondType(bondType);
	const base = getAdminAccountFormValidationSchema({ requirePaymentAndPerformanceBonds }).extend({
		bondTerms: CreateQuote.optionalBondTermsSchema,
		bondType: z.enum(bondRequestBondTypeDef).nullish(),
		contractBondType: z.enum(contractBondSubtypeDef).nullish(),
		commercialBond: isCommercialBond
			? commercialBondSubmitSchema
			: adminCombinedFormSchema.shape.commercialBond,
	});

	if (!commercialSuretyEnabled) {
		return withPrincipalMatchesCompany(base.extend({ quote: quoteSchema }));
	}

	if (isCommercialBond) {
		return base.extend({
			bondType: z.enum(bondRequestBondTypeDef, { error: "Required" }),
			quote: z.any().optional(),
		});
	}

	return withPrincipalMatchesCompany(
		base
			.extend({
				bondType: z.enum(bondRequestBondTypeDef, { error: "Required" }),
				quote: quoteSchema,
			})
			.superRefine((val, ctx) => {
				if (isContractCategoryBondType(val.bondType) && !val.contractBondType) {
					ctx.addIssue({
						code: "custom",
						path: ["contractBondType"],
						message: "Required",
					});
				}
			}),
	);
};

export type AdminAccountFormData = z.infer<typeof adminAccountFormSchema>;
export type AdminCombinedFormData = z.infer<typeof adminCombinedFormSchema>;

export type CompanyFormData = Omit<
	z.infer<typeof Dtos.Company.Create.FromSubmission.Request.schema>,
	"address"
> & {
	address: Address | null | undefined;
};

export type OwnerFormData = Omit<
	z.infer<typeof Dtos.Individual.Create.Request.ownerSchema>,
	"address"
> & {
	address?: Address | undefined | null;
};

export type SpouseFormData = Omit<
	z.infer<typeof Dtos.Individual.Create.Request.individualSchema>,
	"address"
> & {
	address?: Address | undefined | null;
};

export type IndividualFormData = OwnerFormData | SpouseFormData;
export type IndividualGroup = IndividualFormData[];

export const createDefaultOwner = (): Partial<OwnerFormData> & { type: "owner" } => ({
	type: "owner",
});

export const createDefaultSpouse = (): Partial<SpouseFormData> => ({});

export const fp = (path: Path<AdminCombinedFormData>): string => path;

// Unanswered means the question is hidden behind the commercial surety flag, which preserves the
// pre-commercial contract behavior.
export const toAdminAccountCreateRequest = (
	data: AdminCombinedFormData,
): Dtos.Admin.SuretyAccount.Create.Request => {
	const {
		agencyId,
		accountTerms,
		indemnityAgreement,
		bondType,
		contractBondType: _contractBondType,
		commercialBond: _commercialBond,
		quote,
		requiresPaymentAndPerformanceBonds,
		...rest
	} = data;
	return {
		...rest,
		aiEnabled: true,
		account: {
			...rest.account,
			requiresPaymentAndPerformanceBonds: requiresPaymentAndPerformanceBonds ?? true,
		},
		terms: accountTerms ?? undefined,
		indemnityAgreement: indemnityAgreement ? { fileId: indemnityAgreement.fileId } : undefined,
		// TODO (DEV-3411 / DEV-3613): persist commercial quotes created with the account.
		...(isCommercialSuretyBondType(bondType) ? {} : { quote }),
	};
};

export const withResolvedPrincipal = (data: AdminCombinedFormData): AdminCombinedFormData => {
	const name = resolvePrincipalName({
		companies: data.account?.details?.companies,
		principalName: data.quote?.principal?.name,
	});
	if (name === undefined || name === data.quote?.principal?.name) return data;
	return { ...data, quote: { ...data.quote, principal: { ...data.quote?.principal, name } } };
};

export const getDefaultAdminAccountFormData = (): AdminAccountFormData =>
	getSchemaDefaults(adminAccountFormSchema);
export const getDefaultAdminCombinedFormData = (): AdminCombinedFormData => {
	const defaults = getSchemaDefaults(adminCombinedFormSchema);
	defaults.quote.suretyType = "contract";
	return defaults;
};
